import type { ColumnOptions, GetColumnOptions } from 'client/js/main/selectors/Analytics/createColumnsSelector.types';
import type { RootState } from 'client/js/redux';
import { createSelector } from 'reselect';
import { MAX_APP_CALCULATIONS, MAX_USER_CALCULATIONS } from '../../../../shared/constants/customCalculationConstants';
import { CUSTOM_CALCULATION_DATA_FIELD_KEY_PREFIX } from '../../../../shared/constants/dataFieldKeys';
import { COLUMN_TYPE } from '../../common/support/utils';
import { buildColumn } from '../../common/support/utils/buildColumn';
import type { IColumn } from '../../common/types/IColumn';
import { FORMATTER } from '../../common/utils';
import type { CustomCalculationReducer } from '../reducers/CustomCalculationReducer';
import type { CustomCalculationRecord } from '../types/customCalculations';

const EMPTY_ARRAY = [];

const getCustomCalculationsState = (state: RootState): CustomCalculationReducer => state.customCalculations;
// biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
const getCalculationId = (state: RootState, calculationId?: string) => calculationId;

export const getCustomCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  getCustomCalculationsState,
  (customCalculationsState) => {
    return customCalculationsState?.records ? Object.values(customCalculationsState.records) : EMPTY_ARRAY;
  }
);

export const getCustomCalculationColumns = (records: CustomCalculationRecord[], options?: ColumnOptions): IColumn[] => {
  return records.map(({ id, name, definition, isAppLevel }) => {
    const key = `${CUSTOM_CALCULATION_DATA_FIELD_KEY_PREFIX}${id}`;
    const type = COLUMN_TYPE.CUSTOM_CALCULATION;

    const equation = definition.equation;
    const formatter = FORMATTER[definition.format];
    const popover = {
      title: `${name}: Custom ${isAppLevel ? 'App' : 'User'} calculation`,
    };
    const display = name;

    return buildColumn({
      type,
      key,
      overrides: { display, popover, formatter, id, equation, isAppLevel },
      extensions: { ...options?.extensions, FORMAT: definition.format },
      localCurrency: options?.localCurrency,
    });
  });
};

export const createCustomCalculationColumnsSelector = (getColumnOptions: GetColumnOptions = () => undefined) => {
  return createSelector(getCustomCalculationRecords, getColumnOptions, getCustomCalculationColumns);
};

export const getCalculationRecord: (state: RootState, calculationId?: string) => undefined | CustomCalculationRecord =
  createSelector(getCustomCalculationsState, getCalculationId, (customCalculationsState, calculationId) => {
    const records = customCalculationsState?.records || {};
    if (!calculationId || !records[calculationId]) {
      return;
    }
    return records[calculationId];
  });

export const getUserCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  getCustomCalculationRecords,
  (records) => records.filter((calculationRecord) => !calculationRecord.isAppLevel)
);

export const getAppCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  getCustomCalculationRecords,
  (records) => records.filter((calculationRecord) => calculationRecord.isAppLevel)
);

export const getCanCreateUserCalculation: (state: RootState) => boolean = createSelector(
  getUserCalculationRecords,
  (userCalculationRecords) => userCalculationRecords.length < MAX_USER_CALCULATIONS
);

export const getCanCreateAppCalculation: (state: RootState) => boolean = createSelector(
  getAppCalculationRecords,
  (appCalculationRecords) => appCalculationRecords.length < MAX_APP_CALCULATIONS
);
